import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ErrorComponent from 'components/shared/ErrorComponent';
import { Paginator } from 'components/shared/Paginator';
import { Loader } from 'components/shared/Loader';
import axiosPixLink from 'config/axiosPixLink';
import { UserSessionSelector } from 'store/UserSession';
import { CAMPAIGN_STATUS_SOURCING_LABELS, PIXLINK_ROLES, FILTER_CAMPAIGN_TYPE_LIST_GLOBAL_VIEW } from 'utils/Constants';
import { formatDate } from 'utils/DateUtils';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import { CustomDropdownSingleRedactionPublicationSpot } from 'components/shared/DropDown/CustomDropdownStatusSpots';
import { Modal } from 'reactstrap';
import 'pages/SuperAdmin/order.css';
import { classes } from 'components/shared/modal/modalClasses';

const pageNbOptions = [10, 25, 50, 100];

// Rôles autorisés à voir cette page
const ALLOWED_ROLES = [PIXLINK_ROLES.ADMIN, PIXLINK_ROLES.SUPER_ADMIN, PIXLINK_ROLES.CONSULTANT];

const COLUMNS = [
    { label: 'N° devis', field: 'numeroDevis', sortable: true, type: 'string' },
    { label: 'Client', field: 'client', sortable: true, type: 'string' },
    { label: 'Consultant FR', field: 'consultantFR', sortable: false, type: 'string' },
    { label: 'Responsable', field: 'responsable', sortable: false, type: 'string' },
    { label: 'Type', field: 'type', sortable: true, type: 'string' },
    { label: 'Fin de contrat', field: 'finDeContrat', sortable: true, type: 'date' },
    { label: 'Statut', field: 'campaignStatus', sortable: true, type: 'string' },
    { label: 'Nb à commander', field: 'nbACommander', sortable: true, type: 'number' },
    { label: 'Nb en rédaction', field: 'nbEnRedaction', sortable: true, type: 'number' },
    { label: 'Nb à valider Netlinking', field: 'nbAValiderNetlinking', sortable: true, type: 'number' },
    { label: 'Nb en cours de validation client', field: 'nbEnCoursValidationClient', sortable: true, type: 'number' },
    { label: 'Nb validés client, à publier', field: 'nbValideClientAPublier', sortable: true, type: 'number' },
    { label: 'Nb en cours de publication', field: 'nbEnCoursPublication', sortable: true, type: 'number' },
    { label: 'Nb publiés', field: 'nbPublies', sortable: true, type: 'number' },
    { label: 'Remarque', field: 'remarque', sortable: false, type: 'string' },
];

// ── Types de campagne : valeurs réelles utilisées côté back ─────────
const CAMPAIGN_TYPE_OPTIONS = [
    { text: 'Standard', status: 'standard' },
    { text: 'Premium', status: 'premium' },
    { text: 'Ultra Premium', status: 'ultraPremium' },
    { text: 'Champ libre', status: 'freeText' },
];

// ── Les 11 statuts exacts attendus par le back (sensible à la casse) ─
const CAMPAIGN_STATUS_KEYS = [
    'CAMPAIGN_STARTED_TO_BE_SELECTED_BY_CONSULTANT',
    'CAMPAIGN_NOTIFIED_SUPER_ADMIN_TO_BE_APPROVED',
    'CAMPAIGN_NOTIFIED_SUPER_CONSULTANT_TO_BE_APPROVED',
    'CAMPAIGN_NOTIFIED_SUPER_ADMIN_SUPER_CONSULTANT_TO_BE_APPROVED',
    'CAMPAIGN_NOTIFIED_CLIENT_TO_BE_VALIDATED',
    'CAMPAIGN_NOTIFIED_CONSULTANT_TO_BE_FILTERED',
    'CAMPAIGN_NOTIFIED_EDITOR_TO_BE_COMMANDED',
    'CAMPAIGN_ANCRAGE_TO_BE_PREPARED',
    'CAMPAIGN_ANCRAGE_VALIDATED',
    'CAMPAIGN_BROUILLON',
    'CAMPAIGN_EN_LIGNE',
];

const getTypeDisplay = (type) => {
    const match = CAMPAIGN_TYPE_OPTIONS.find((o) => o.status === type);
    return match ? match.text : (type || '-');
};

const getStatusLabel = (t, campaignStatus) => {
    if (!campaignStatus) return '-';
    const label = CAMPAIGN_STATUS_SOURCING_LABELS?.[campaignStatus];
    return label ? t(label) : campaignStatus;
};

// Essaie plusieurs formes de champ nom, selon ce que renvoie l'API user
const getPersonName = (person) => {
    if (!person) return '';
    if (person.nom || person.prenom) return `${person.prenom || ''} ${person.nom || ''}`.trim();
    if (person.firstName || person.lastName) return `${person.firstName || ''} ${person.lastName || ''}`.trim();
    if (person.name) return person.name;
    if (person.email) return person.email;
    return String(person.id);
};

export default function VueRapport() {
    const { t } = useTranslation();
    const { user } = useSelector(UserSessionSelector);
    const history = useHistory();
    const fetchedRef = useRef(false);

    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Listes de référence pour Consultant FR / Responsable
    const [superConsultants, setSuperConsultants] = useState([]); // -> consultantFRIds
    const [consultants, setConsultants] = useState([]); // -> responsableIds

    const [titleSearch, setTitleSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage, setOrdersPerPage] = useState(10);
    const [currentOrders, setCurrentOrders] = useState([]);
    const [pageNumbers, setPageNumbers] = useState([]);

    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [sortedCache, setSortedCache] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedType, setSelectedType] = useState(FILTER_CAMPAIGN_TYPE_LIST_GLOBAL_VIEW[0]);

    const handleClick = (index, event) => setAnchorEl({ [index]: event.currentTarget });
    const handleClose = () => setAnchorEl(null);

    // Sauvegarde générique (savingId réutilisé pour la popin remarque + tous les champs)
    const [savingId, setSavingId] = useState(null);
    const [fieldError, setFieldError] = useState({ id: null, message: '' });

    // ── Popin remarque ────────────────────────────────────────────────
    const [commentairePopinOpen, setCommentairePopinOpen] = useState(false);
    const [commentaireCampaignId, setCommentaireCampaignId] = useState(null);
    const [commentaireDraft, setCommentaireDraft] = useState('');

    // ── Dropdowns champs éditables (Type / Statut) ──────────────────────
    const [statutAnchorEl, setStatutAnchorEl] = useState(null);
    const [typeAnchorEl, setTypeAnchorEl] = useState(null);


    const [consultantFRAnchorEl, setConsultantFRAnchorEl] = useState(null);
    const [responsableAnchorEl, setResponsableAnchorEl] = useState(null);

    // ── Garde-fou côté front sur les rôles autorisés ──────────────────
    useEffect(() => {
        const role = user?.role ?? user?.roles?.[0] ?? '';
        if (role && !ALLOWED_ROLES.includes(role)) {
            history.push('/');
        }
    }, [user, history]);

    useEffect(() => {
        document.title = t('PixLink - Vue Rapport');
    }, []);

    // ── Chargement des données (campagnes + listes de personnes) ───────
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        setIsLoading(true);
        setIsError(false);

        Promise.all([
            axiosPixLink.post('/campaign/rapport-thailande'),
            axiosPixLink.get('/user/superconsultants'),
            axiosPixLink.get('/user/consultants'),
        ])
            .then(([campaignsRes, superConsultantsRes, consultantsRes]) => {
                setCampaigns(Array.isArray(campaignsRes.data) ? campaignsRes.data : []);
                setSuperConsultants(Array.isArray(superConsultantsRes.data) ? superConsultantsRes.data : []);
                setConsultants(Array.isArray(consultantsRes.data) ? consultantsRes.data : []);
                setIsLoading(false);
            })
            .catch(() => {
                setIsError(true);
                setIsLoading(false);
            });
    }, []);

    const statutList = useMemo(() => {
        return CAMPAIGN_STATUS_KEYS.map((key) => ({
            text: t(CAMPAIGN_STATUS_SOURCING_LABELS?.[key] || key),
            status: key,
        }));
    }, [t]);

    // Affichage "Nom1, Nom2" pour une ligne à partir des ids
    const namesForIds = (ids, list) => {
        if (!Array.isArray(ids) || ids.length === 0) return '-';
        const names = ids
            .map((id) => list.find((p) => p.id === id))
            .filter(Boolean)
            .map(getPersonName);
        return names.length > 0 ? names.join(', ') : '-';
    };

    // ── Filtrage (recherche libre) ──────────────────────────────────────
    const filteredOrders = useMemo(() => {
        return campaigns.filter((c) => {
            if (titleSearch) {
                const q = titleSearch.toLowerCase();
                const consultantNames = namesForIds(c.consultantFRIds, superConsultants);
                const responsableNames = namesForIds(c.responsableIds, consultants);
                const hay = [c.numeroDevis, c.client, consultantNames, responsableNames]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (selectedType.value) {
                const normalizedType = (c.type || 'standard').toLowerCase();
                if (normalizedType !== selectedType.value) return false;
            }
            return true;
        });
    }, [campaigns, titleSearch, selectedType, superConsultants, consultants]);

    // ── Tri ───────────────────────────────────────────────────────────
    const handleSortingChange = (field) => {
        const direction = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
        const col = COLUMNS.find((c) => c.field === field);
        const sorted = [...filteredOrders].sort((a, b) => {
            let aVal = a[field];
            let bVal = b[field];
            if (col?.type === 'date') {
                aVal = aVal ? new Date(aVal).getTime() : -Infinity;
                bVal = bVal ? new Date(bVal).getTime() : -Infinity;
                return direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            if (col?.type === 'number') {
                aVal = aVal ?? -Infinity;
                bVal = bVal ?? -Infinity;
                return direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            aVal = (aVal || '').toString().toLowerCase();
            bVal = (bVal || '').toString().toLowerCase();
            return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
        setSortField(field);
        setSortOrder(direction);
        setSortedCache(sorted);
    };

    useEffect(() => { setSortedCache(null); }, [filteredOrders]);
    const effectiveOrders = sortedCache || filteredOrders;

    useEffect(() => { setCurrentPage(1); }, [titleSearch, selectedType]);

    useEffect(() => {
        const last = currentPage * ordersPerPage;
        const first = last - ordersPerPage;
        setCurrentOrders(effectiveOrders.slice(first, last));
        setPageNumbers([...Array(Math.ceil(effectiveOrders.length / ordersPerPage)).keys()].map((i) => i + 1));
    }, [currentPage, effectiveOrders, ordersPerPage]);

    useEffect(() => {
        if (pageNumbers.length > 0 && pageNumbers[pageNumbers.length - 1] < currentPage) {
            setCurrentPage(pageNumbers[pageNumbers.length - 1]);
        }
    }, [pageNumbers]);

    // ── Sauvegarde d'un seul champ, en respectant le contrat du back ────
    // Le endpoint veut TOUJOURS les 5 champs ensemble ; on part donc de
    // l'état actuel de la campagne et on n'écrase que celui qui a changé.
    const saveOneField = async (campaignId, field, value) => {
        const campaign = campaigns.find((c) => c.id === campaignId);
        if (!campaign) return;

        const payload = {
            remarque: campaign.remarque ?? null,
            type: campaign.type ?? 'standard',
            campaignStatus: campaign.campaignStatus,
            consultantFRIds: campaign.consultantFRIds || [],
            responsableIds: campaign.responsableIds || [],
            [field]: value, // seul le champ modifié est écrasé
        };

        setSavingId(campaignId);
        setFieldError({ id: null, message: '' });
        try {
            await axiosPixLink.post(`/campaign/${campaignId}/rapport-champs-manuels`, payload);
            const applyUpdate = (list) =>
                list.map((c) => (c.id === campaignId ? { ...c, ...payload } : c));
            setCampaigns((prev) => applyUpdate(prev));
            if (sortedCache) setSortedCache((prev) => applyUpdate(prev));
        } catch (error) {
            const message =
                error?.response?.status === 400
                    ? t("Valeur invalide, rien n'a été enregistré.")
                    : t("Erreur lors de l'enregistrement.");
            setFieldError({ id: campaignId, message });
            console.error(`Erreur lors de la sauvegarde du champ ${field}:`, error);
        } finally {
            setSavingId(null);
        }
    };

    // ── Consultant FR / Responsable (tableaux d'ids, cases à cocher) ────
    const toggleConsultantFR = (campaignId, personId) => {
        const campaign = campaigns.find((c) => c.id === campaignId);
        const current = campaign?.consultantFRIds || [];
        const next = current.includes(personId)
            ? current.filter((id) => id !== personId)
            : [...current, personId];
        saveOneField(campaignId, 'consultantFRIds', next);
    };

    const toggleResponsable = (campaignId, personId) => {
        const campaign = campaigns.find((c) => c.id === campaignId);
        const current = campaign?.responsableIds || [];
        const next = current.includes(personId)
            ? current.filter((id) => id !== personId)
            : [...current, personId];
        saveOneField(campaignId, 'responsableIds', next);
    };

    const handleStatutChange = (campaignId, value) => {

        if (!value) {
            setStatutAnchorEl(null);
            return;
        }
        saveOneField(campaignId, 'campaignStatus', value);
        setStatutAnchorEl(null);
    };

    const handleTypeChange = (campaignId, value) => {
        if (!value) {
            setTypeAnchorEl(null);
            return;
        }

        const typeToSave = value === 'freeText' ? '' : value;
        saveOneField(campaignId, 'type', typeToSave);
        setTypeAnchorEl(null);
    };


    // ── Popin remarque ────────────────────────────────────────────────
    const openCommentairePopin = (campaign) => {
        setCommentaireCampaignId(campaign.id);
        setCommentaireDraft(campaign.remarque || '');
        setCommentairePopinOpen(true);
    };

    const closeCommentairePopin = () => {
        setCommentairePopinOpen(false);
        setCommentaireCampaignId(null);
        setCommentaireDraft('');
    };

    const saveCommentairePopin = async () => {
        if (commentaireCampaignId == null) return;
        await saveOneField(commentaireCampaignId, 'remarque', commentaireDraft);
        closeCommentairePopin();
    };

    return (
        <div className="content-center">
            <style>{`
            .MuiMenu-paper {
                max-height: 340px !important;
                overflow-y: auto !important;
            }
        `}</style>
            <div className="box-info-header">
                <div className="b-child-1">
                    <h1>{t('Vue Rapport')}</h1>
                    <div className="box-header-top-dm header-info">
                        <h1>{effectiveOrders.length} {t('Campagne(s) trouvée(s)')}</h1>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="input-container-excel">
                <div className="input-group input-cs" style={{ flex: '1 1 0', minWidth: '160px' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t('Rechercher (n° devis, client, consultant, responsable...)')}
                        value={titleSearch}
                        onChange={(e) => setTitleSearch(e.target.value)}
                    />
                </div>

                <div className="d-flex align-items-center item-per-table" style={{ flexShrink: 0 }}>
                    <span className="me-2">{t('Afficher les résultats')}</span>
                    <div className="dropdown" style={{ width: '70px' }}>
                        <button className="btn dropdown-toggle dropdown-custom w-100" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            {ordersPerPage}
                            <span><i className="bi bi-chevron-down" /></span>
                        </button>
                        <ul className="dropdown-menu">
                            {pageNbOptions.filter((nb) => nb !== ordersPerPage).map((item, idx) => (
                                <li key={idx} onClick={() => { setOrdersPerPage(item); setCurrentPage(1); }}>
                                    <button className="dropdown-item">{item}</button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <br />

            {/* ── Table ── */}
            {isLoading ? (
                <Loader />
            ) : isError ? (
                <ErrorComponent />
            ) : (
                <div className="box-wrapper">
                    <div className="table-responsive">
                        <div className="box-table-netlinking">
                            <table className="table-lists table-commandes">
                                <thead>
                                    <tr>
                                        {COLUMNS.map(({ label, field, sortable }) => (
                                            <th
                                                key={field}
                                                style={{ cursor: sortable && field !== 'type' ? 'pointer' : 'default' }}
                                                onClick={sortable && field !== 'type' ? () => handleSortingChange(field) : undefined}
                                            >
                                                {t(label)}
                                                {sortable && field !== 'type' && (
                                                    <i
                                                        style={{ marginLeft: '3px' }}
                                                        className={field === sortField && sortOrder === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}
                                                    />
                                                )}
                                                {field === 'type' && (
                                                    <OrderTypeFilter
                                                        orderTypeList={FILTER_CAMPAIGN_TYPE_LIST_GLOBAL_VIEW}
                                                        selectedType={selectedType}
                                                        setSelectedType={(val) => { setSelectedType(val); setCurrentPage(1); }}
                                                        handleClick={handleClick}
                                                        handleClose={handleClose}
                                                        anchorEl={anchorEl}
                                                    />
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentOrders.length === 0 ? (
                                        <tr>
                                            <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">
                                                {t('Aucune campagne trouvée')}
                                            </td>
                                        </tr>
                                    ) : (
                                        currentOrders.map((c) => (
                                            <tr key={c.id}>
                                                <td>{c.numeroDevis || '-'}</td>
                                                <td>{c.client || '-'}</td>

                                                {/* Consultant FR — cases à cocher (multi-select) */}
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setConsultantFRAnchorEl({ [c.id]: e.currentTarget });
                                                        }}
                                                        style={{ padding: '5px', border: 'none', background: 'none', cursor: 'pointer' }}
                                                        disabled={savingId === c.id}
                                                    >
                                                        {namesForIds(c.consultantFRIds, superConsultants)}
                                                    </button>
                                                    <CustomDropdownSingleRedactionPublicationSpot
                                                        handleClose={() => setConsultantFRAnchorEl(null)}
                                                        anchorEl={consultantFRAnchorEl && consultantFRAnchorEl[c.id]}
                                                        list={superConsultants.map((p) => ({ text: getPersonName(p), status: p.id }))}
                                                        selectedStatus="" // multi-select: no single "selected" value to highlight
                                                        multiple
                                                        selectedValues={c.consultantFRIds || []}
                                                        toggleStatus={(personId) => toggleConsultantFR(c.id, personId)}
                                                        getOptionLabel={(option) => option.text}
                                                    />
                                                </td>

                                                {/* Responsable — cases à cocher (multi-select) */}
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setResponsableAnchorEl({ [c.id]: e.currentTarget });
                                                        }}
                                                        style={{ padding: '5px', border: 'none', background: 'none', cursor: 'pointer' }}
                                                        disabled={savingId === c.id}
                                                    >
                                                        {namesForIds(c.responsableIds, consultants)}
                                                    </button>
                                                    <CustomDropdownSingleRedactionPublicationSpot
                                                        handleClose={() => setResponsableAnchorEl(null)}
                                                        anchorEl={responsableAnchorEl && responsableAnchorEl[c.id]}
                                                        list={consultants.map((p) => ({ text: getPersonName(p), status: p.id }))}
                                                        selectedStatus=""
                                                        multiple
                                                        selectedValues={c.responsableIds || []}
                                                        toggleStatus={(personId) => toggleResponsable(c.id, personId)}
                                                        getOptionLabel={(option) => option.text}
                                                    />
                                                </td>

                                                {/* Type — éditable via dropdown (liste + champ libre) */}
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setTypeAnchorEl({ [c.id]: e.currentTarget });
                                                        }}
                                                        style={{ padding: '5px', border: 'none', background: 'none', cursor: 'pointer' }}
                                                        disabled={savingId === c.id}
                                                    >
                                                        {getTypeDisplay(c.type)}
                                                    </button>
                                                    <CustomDropdownSingleRedactionPublicationSpot
                                                        selectedStatus={
                                                            CAMPAIGN_TYPE_OPTIONS.find((o) => o.status === c.type)?.status || ''
                                                        }
                                                        handleClose={() => setTypeAnchorEl(null)}
                                                        anchorEl={typeAnchorEl && typeAnchorEl[c.id]}
                                                        list={CAMPAIGN_TYPE_OPTIONS}
                                                        toggleStatus={(value) => handleTypeChange(c.id, value)}
                                                        getOptionLabel={(option) => option.text}
                                                    />
                                                </td>

                                                <td>{c.finDeContrat ? formatDate(c.finDeContrat) : '-'}</td>

                                                {/* Statut — lecture seule */}
                                                <td>
                                                    {getStatusLabel(t, c.campaignStatus)}
                                                </td>

                                                <td>{c.nbACommander ?? 0}</td>
                                                <td>{c.nbEnRedaction ?? 0}</td>
                                                <td>{c.nbAValiderNetlinking ?? 0}</td>
                                                <td>{c.nbEnCoursValidationClient ?? 0}</td>
                                                <td>{c.nbValideClientAPublier ?? 0}</td>
                                                <td>{c.nbEnCoursPublication ?? 0}</td>
                                                <td>{c.nbPublies ?? 0}</td>

                                                {/* Remarque — icône + popin */}
                                                <td>
                                                    <span
                                                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                                        onClick={() => openCommentairePopin(c)}
                                                    >
                                                        <img
                                                            src="Images/commentaire.png"
                                                            alt="icon-commentaire"
                                                            style={{ width: '18px', marginBottom: '5px', opacity: c.remarque ? 1 : 0.4 }}
                                                        />
                                                        {c.remarque
                                                            ? (c.remarque.length > 20 ? `${c.remarque.substring(0, 20)}...` : c.remarque)
                                                            : '-'}
                                                    </span>
                                                    {fieldError.id === c.id && (
                                                        <div className="text-danger" style={{ fontSize: '0.75rem' }}>
                                                            {fieldError.message}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <Paginator
                isLoading={isLoading}
                listLength={effectiveOrders.length}
                currentPage={currentPage}
                pageNumbers={pageNumbers}
                nbElementsPerPage={ordersPerPage}
                goToPage={(page) => setCurrentPage(page)}
            />

            {/* ── Popin remarque ── */}
            {commentairePopinOpen && (
                <Modal
                    style={classes.modalCustom}
                    isOpen={commentairePopinOpen}
                    backdrop={true}
                >
                    <div style={classes.modalHeader} className="modal-header">
                        <h5 style={classes.title} className="modal-title">
                            {t('Remarque')}
                        </h5>
                        <button
                            type="button"
                            onClick={closeCommentairePopin}
                            style={classes.btnClose}
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        />
                    </div>
                    <div style={classes.modalPad} className="modal-body">
                        <div className="row row-mb-lg">
                            <div className="col-12 col-md-12">
                                <textarea
                                    className="form-control"
                                    rows={5}
                                    value={commentaireDraft}
                                    onChange={(e) => setCommentaireDraft(e.target.value)}
                                    disabled={savingId === commentaireCampaignId}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div style={classes.boxBtConfirm}>
                            <button
                                style={{ backgroundColor: '#ee3900' }}
                                onClick={closeCommentairePopin}
                                className="confirm me-2"
                                disabled={savingId === commentaireCampaignId}
                            >
                                {t('Annuler')}
                            </button>
                            <button
                                style={{ backgroundColor: '#43b929' }}
                                onClick={saveCommentairePopin}
                                className="confirm"
                                disabled={savingId === commentaireCampaignId}
                            >
                                {t('Enregistrer')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}